import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { Switch, FormControlLabel } from '@mui/material';
import { apiPost } from '../services/apiClient';
import { fillBucketGaps } from '../utils/timeSeriesBucketing';
import { formatCompactNumber, formatFullNumber } from '../utils/formatNumber';

// Combined YouTube Views + Comments chart. Views are bars on the LEFT axis;
// comments are a line on a SECONDARY (right) axis so they stay readable despite
// being orders of magnitude smaller. Comments are toggleable (default on); when
// off, both the line and the right axis disappear.
//
// NOTE: the two axes are independently scaled, so the series' relative heights
// are NOT comparable — this shows each trend over time, not "comments vs views"
// magnitude. Both are pulled from topic_metrics_by_source_daily (the same
// amortized recheck grain as every other chart on this page), scoped to the
// youtube source (prefix match, so 'youtube'/'youtube_video' both count).

const VIEWS_COLOR = '#8884d8';
const COMMENTS_COLOR = '#ff7300';

// Bucket ascending {fullDate, views, comments} rows into day/week/month,
// summing BOTH series within each bucket. ('day' passes rows through.)
function bucketViewsComments(rows, bucketPeriod) {
    if (bucketPeriod !== 'week' && bucketPeriod !== 'month') return rows;

    const keyOf = (d) => bucketPeriod === 'week'
        ? (() => { const s = new Date(d); s.setDate(s.getDate() - s.getDay()); return s; })()
        : parseISO(format(d, 'yyyy-MM-01'));

    const buckets = [];
    let curKey = null, cur = null;
    rows.forEach(item => {
        const start = keyOf(parseISO(item.fullDate));
        const k = start.getTime();
        if (curKey !== k) {
            if (cur) buckets.push(cur);
            cur = {
                date: format(start, bucketPeriod === 'week' ? 'MMM dd' : 'MMM yyyy'),
                fullDate: format(start, 'yyyy-MM-dd'),
                views: 0, comments: 0,
            };
            curKey = k;
        }
        cur.views += item.views;
        cur.comments += item.comments;
    });
    if (cur) buckets.push(cur);
    return buckets;
}

const YouTubeViewsCommentsChart = ({
    selectedProduct, topicMap, companyName,
    startDate, endDate, bucketPeriod,
    chartTitle = 'YouTube Views & Comments',
    fillEmptyBuckets = false, chartHeight = 500,
}) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showComments, setShowComments] = useState(true);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    const productName = (selectedProduct && topicMap?.[selectedProduct]?.name) || 'Product';

    const handleSearch = useCallback(async () => {
        if (!selectedProduct) return;
        setIsLoading(true);
        try {
            // One by-source fetch per metric; keep only youtube rows and sum per day.
            const fetchMetric = async (metric) => {
                const resp = await apiPost('topic/engagement/by-source', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    metric,
                });
                if (!resp.ok) throw new Error(`Backend error: ${resp.status}`);
                const json = await resp.json();
                const byDay = {};
                (json.data ?? []).forEach(item => {
                    if (!(item.source || '').toLowerCase().startsWith('youtube')) return;
                    const value = typeof item.value === 'bigint'
                        ? Number(item.value.toString()) : Number(item.value);
                    byDay[item.day] = (byDay[item.day] || 0) + value;
                });
                return byDay;
            };

            const [viewsByDay, commentsByDay] = await Promise.all([
                fetchMetric('views'),
                fetchMetric('comments'),
            ]);

            const days = Array.from(new Set([
                ...Object.keys(viewsByDay), ...Object.keys(commentsByDay),
            ])).sort();

            if (days.length === 0) { setData([]); return; }

            const rows = days.map(day => ({
                date: format(parseISO(day), 'MMM dd'),
                fullDate: day,
                views: viewsByDay[day] || 0,
                comments: commentsByDay[day] || 0,
            }));

            let bucketed = bucketViewsComments(rows, bucketPeriod);
            if (fillEmptyBuckets) {
                bucketed = fillBucketGaps(bucketed, startDate, endDate, bucketPeriod, { views: 0, comments: 0 });
            }
            setData(bucketed);
        } catch (error) {
            console.error('[YouTubeViewsCommentsChart]', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProduct, startDate, endDate, bucketPeriod, fillEmptyBuckets]);

    // Auto-fetch on any shared-input change (embedded — no per-chart Search button).
    useEffect(() => {
        if (selectedProduct) handleSearch();
    }, [handleSearch, selectedProduct]);

    const subline = useMemo(() => {
        if (!startDate || !endDate) return '';
        const range = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        const bucketLabel = { day: 'Daily', week: 'Weekly', month: 'Monthly' }[bucketPeriod] || 'Daily';
        return `${range} • ${bucketLabel}`;
    }, [startDate, endDate, bucketPeriod]);

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card — mirrors the sibling charts' header, with the
                comments toggle in place of a filter bar. */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa', borderRadius: 8, padding: '20px 24px 18px',
                    marginBottom: 20,
                    boxShadow: isHeaderHovered ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)',
                    border: '1px solid rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s ease',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
                }}
            >
                <div>
                    <div style={{ fontSize: 11, color: '#777', fontWeight: 500, lineHeight: 1.2, marginBottom: 1 }}>
                        {companyName || 'Company'}
                    </div>
                    <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111', lineHeight: 1.25 }}>
                        {selectedProduct ? `${productName} · ${chartTitle}` : 'Select a Product'}
                    </h1>
                    {selectedProduct && subline && (
                        <div style={{ fontSize: 12, color: '#999', lineHeight: 1.3, marginTop: 2 }}>{subline}</div>
                    )}
                    <div style={{ fontSize: 11, color: '#777', lineHeight: 1.3, marginTop: 4, fontStyle: 'italic' }}>
                        Views (left axis) and comments (right axis) use independent scales — compare each trend over time, not their heights.
                    </div>
                </div>
                <FormControlLabel
                    control={<Switch checked={showComments} onChange={(e) => setShowComments(e.target.checked)} size="small" />}
                    label={<span style={{ fontSize: 12, color: '#555' }}>Comments</span>}
                    sx={{ marginLeft: 0, flex: '0 0 auto' }}
                />
            </div>

            {/* Chart */}
            {!isLoading && data.length === 0 ? (
                <div style={{
                    marginTop: 8, width: '100%', height: 200, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                    color: '#777', fontSize: 14, backgroundColor: '#fafafa',
                    borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)',
                }}>
                    No YouTube views/comments for this topic in the selected range.
                </div>
            ) : (
                <div style={{ marginTop: 8, width: '100%', height: chartHeight }}>
                    <ResponsiveContainer>
                        <ComposedChart data={data} margin={{ left: 20, right: showComments ? 20 : 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} />
                            <YAxis
                                yAxisId="left"
                                tickFormatter={formatCompactNumber}
                                label={{ value: 'Number of Views', angle: -90, position: 'insideLeft', dx: -10, style: { textAnchor: 'middle' } }}
                            />
                            {showComments && (
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={formatCompactNumber}
                                    label={{ value: 'Number of Comments', angle: 90, position: 'insideRight', dx: 10, style: { textAnchor: 'middle' } }}
                                />
                            )}
                            <Tooltip formatter={(value) => formatFullNumber(value)} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="views" fill={VIEWS_COLOR} name="YouTube Views" />
                            {showComments && (
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="comments"
                                    stroke={COMMENTS_COLOR}
                                    strokeWidth={2}
                                    name="YouTube Comments"
                                    dot={{ fill: COMMENTS_COLOR, strokeWidth: 1, r: 2 }}
                                    activeDot={{ r: 6 }}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default YouTubeViewsCommentsChart;
